// Angular
import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

// Libraries
import { Subject, takeUntil } from 'rxjs';

// Models
import { AppCleanup } from '../../models';

// Service
import { GlobalService } from '../../services';

// Helpers
import { UtilityHelper } from '../../helpers';

@Component({
  selector: 'file-upload-dialog',
  templateUrl: './file-upload-dialog.component.html',
  styleUrls: ['./file-upload-dialog.component.scss'],
})
export class FileUploadDialogComponent extends AppCleanup implements OnInit, OnDestroy {
  componentTitle: string = 'Upload a file';
  isMobile: boolean = false;
  allowedExtensions: string[] = ['.png', '.jpg', '.jpeg', '.csv', '.pdf', '.doc', '.docx', '.xlsx', '.xls'];
  uploadActions: string[] = ['file', 'a file', 'file', 'Select', 'Upload'];
  maxSize = 1024 * 1024 * 20; // 20MB
  isImage = false;
  warningMessages: string[];
  newUrl: any;
  isIos = false;
  dropzoneActive: boolean = false;
  selectedFile: File;
  errorMessage: string;

  @ViewChild('fileInput', { static: false }) fileinput: any;
  @ViewChild('fileCapture', { static: false }) fileCapture: any;
  protected readonly destroy = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<FileUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit() {
    // Determines if the device is a mobile device
    this.globalService.isMobile.pipe(takeUntil(this.destroy)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.warningMessages = this.data.warningMessages;
    if (this.data?.uploadSettings?.allowedExtensions) {
      this.allowedExtensions = this.data.uploadSettings.allowedExtensions;
    }

    if (this.data?.uploadSettings?.uploadActions) {
      this.uploadActions = this.data.uploadSettings.uploadActions;
    }

    if (this.data?.uploadSettings?.maxSize) {
      this.maxSize = this.data.uploadSettings.maxSize;
    }

    this.allowedExtensions = this.allowedExtensions.map(function (extension) {
      return extension.toUpperCase();
    });
    this.isIos = UtilityHelper.isIos(); // Checking if iOS
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  onDragOver(event) {
    event.preventDefault();
    this.dropzoneActive = true;
  }

  onDragLeave(event) {
    event.preventDefault();
    this.dropzoneActive = false;
  }

  onDrop(event) {
    event.preventDefault();
    if (event.dataTransfer.files.length > 1) {
      this.selectedFile = null;
      this.errorMessage = 'You can upload one file at a time';
    } else if (event.dataTransfer.files.length === 1) {
      this.selectedFile = event.dataTransfer.files[0];
      this.validateFile();
    }
  }

  openFileUpload(id: string) {
    if (id) {
      document.getElementById(id).click();
    }
  }

  detectFiles(event) {
    if (event.target.files.length > 1) {
      this.selectedFile = null;
      this.errorMessage = 'You can upload one file at a time';
    } else if (event.target.files.length === 1) {
      this.selectedFile = event.target.files[0];
      let reader = new FileReader();
      reader.onload = (e) => (this.newUrl = reader.result);
      reader.readAsDataURL(this.selectedFile);
      this.validateFile();
    }
  }

  validateFile() {
    const size = this.selectedFile.size;
    const extension: string = '.' + this.selectedFile.name.split('.').pop().toUpperCase();
    if (extension === '.jpeg' || extension === '.jpg' || extension === '.png') {
      this.isImage = true;
    }

    if (size > this.maxSize) {
      this.selectedFile = null;
      this.errorMessage = 'File size is too big';
    } else if (this.allowedExtensions.length > 0 && !this.allowedExtensions.includes(extension)) {
      this.selectedFile = null;
      this.errorMessage = 'File extension is not allowed';
    }

    this.dropzoneActive = true;
  }

  clearFile() {
    this.selectedFile = null;
    this.errorMessage = null;
    this.dropzoneActive = false;
    this.newUrl = null;

    setTimeout(() => {
      if (this.fileinput) {
        this.fileinput.nativeElement.value = '';
      }

      if (this.fileCapture) {
        this.fileinput.nativeElement.value = '';
      }
    }, 200);
  }

  submit() {
    let finalFile: File;
    finalFile = this.selectedFile;
    this.dialogRef.close(finalFile);
    this.clearFile();
  }
}

export class FileUploadSettings {
  maxSize: number;
  allowedExtensions: string[];
  uploadActions: string[];
}
